
import React, { useState, useEffect, useRef } from "react";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { CompanyMember } from "./CustomerFormTypes";
import { UseFormReturn } from "react-hook-form";
import { CustomerFormType } from "./CustomerFormTypes";
import { supabase } from "@/integrations/supabase/client";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const LEAD_SOURCES = [
  "Word of Mouth",
  "Website",
  "LocalPros",
  "Bark",
  "Facebook",
  "Instagram",
  "LinkedIn",
  "Car Branding",
  "House Branding",
  "Youtube",
  "Other",
];

type LeadFieldsProps = {
  form: UseFormReturn<CustomerFormType>;
  companyMembers: CompanyMember[];
  watchedLeadSource: string;
};

type CustomerOption = {
  value: string;
  label: string;
};

// Safe content component that always renders within a CommandGroup
const SafeCommandContent: React.FC<{
  data: CustomerOption[] | null | undefined;
  isLoading: boolean;
  error: string | null;
  searchTerm: string;
  onSelect: (value: string) => void;
  selectedValue: string | null;
}> = ({ data, isLoading, error, searchTerm, onSelect, selectedValue }) => {
  // Safety check - if data is not an array, make it an empty array
  const safeData = Array.isArray(data) ? data : [];
  
  // Safely filter data - ADD DEBUG LOGGING
  const filteredData = React.useMemo(() => {
    console.log('SafeCommandContent - Data length:', safeData.length);
    console.log('SafeCommandContent - Search term:', searchTerm);
    
    if (safeData.length === 0) return [];
    if (!searchTerm) {
      console.log('SafeCommandContent - No search term, returning all data:', safeData.length);
      return safeData;
    }
    
    const searchLower = searchTerm.toLowerCase();
    const filtered = safeData.filter(item => 
      item && 
      typeof item.label === 'string' && 
      item.label.toLowerCase().includes(searchLower)
    );
    
    console.log('SafeCommandContent - Filtered data length:', filtered.length);
    return filtered;
  }, [safeData, searchTerm]);

  // Loading state
  if (isLoading) {
    return (
      <CommandEmpty>
        <div className="flex items-center justify-center p-4">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          <span>Loading customers...</span>
        </div>
      </CommandEmpty>
    );
  }

  // Error state
  if (error) {
    return <CommandEmpty>{error}</CommandEmpty>;
  }

  // No data state
  if (safeData.length === 0) {
    return <CommandEmpty>No customers found</CommandEmpty>;
  }

  // No filtered results state
  if (filteredData.length === 0) {
    return <CommandEmpty>No customers match your search</CommandEmpty>;
  }

  // Success state - render the items
  return (
    <>
      {filteredData.map((item) => (
        <CommandItem
          key={item.value}
          value={item.value}
          onSelect={() => onSelect(item.value)}
        >
          {item.label}
          <Check
            className={cn(
              "ml-auto h-4 w-4",
              item.value === selectedValue
                ? "opacity-100"
                : "opacity-0"
            )}
          />
        </CommandItem>
      ))}
    </>
  );
};

export const LeadFields: React.FC<LeadFieldsProps> = ({
  form,
  companyMembers,
  watchedLeadSource,
}) => {
  // Always initialize as an empty array, never undefined
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isReferralOpen, setIsReferralOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Add ref to track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  
  // Add ref for click outside functionality
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsReferralOpen(false);
      }
    };
    
    if (isReferralOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isReferralOpen]);
  
  // Fetch existing customers when needed for referral selection
  useEffect(() => {
    let abortController: AbortController | null = null;
    
    const fetchCustomers = async () => {
      if (watchedLeadSource === "Word of Mouth") {
        try {
          // Create abort controller for this request
          abortController = new AbortController();
          
          // Only set loading if component is still mounted
          if (isMountedRef.current) {
            setError(null);
            setIsLoading(true);
          }
          
          const { data, error } = await supabase
            .from("customers")
            .select("id, name, last_name")
            .order("name")
            .abortSignal(abortController.signal);
            
          // Check if component is still mounted before updating state
          if (!isMountedRef.current) return;
            
          if (error) {
            console.error("Error fetching customers for referrals:", error);
            setError("Failed to load customers. Please try again later.");
            setCustomers([]);
            return;
          }
          
          // Always ensure data is an array before mapping - ADD DEBUG LOGGING
          if (Array.isArray(data) && data.length > 0) {
            console.log('Raw customer data from DB:', data.length, 'customers');
            const formattedCustomers = data
              .filter(cust => cust && cust.id) // Filter out any malformed records
              .map(cust => ({
                value: cust.id,
                label: `${cust.name || ''} ${cust.last_name || ''}`.trim() || 'Unnamed Customer'
              }));
            
            console.log('Formatted customers for dropdown:', formattedCustomers.length);
            setCustomers(formattedCustomers);
          } else {
            // Set empty array if no data
            setCustomers([]);
          }
        } catch (error: any) {
          // Check if component is still mounted and error is not from abort
          if (!isMountedRef.current || error.name === 'AbortError') return;
          
          console.error("Error in fetchCustomers:", error);
          setError("An unexpected error occurred while loading customers.");
          setCustomers([]);
        } finally {
          // Only update loading state if component is still mounted
          if (isMountedRef.current) {
            setIsLoading(false);
          }
        }
      } else {
        // Reset customers if lead source is not Word of Mouth
        if (isMountedRef.current) {
          setCustomers([]);
          setError(null);
          setIsLoading(false);
        }
      }
    };
    
    fetchCustomers();
    
    // Cleanup function to abort request if component unmounts or dependencies change
    return () => {
      if (abortController) {
        abortController.abort();
      }
    };
  }, [watchedLeadSource]);

  // Helper function to get the selected customer label
  const getSelectedCustomerLabel = (value: string | null) => {
    if (!value) return "Select a referring customer";
    
    // Safely check if customers exists and is an array before using find
    if (!Array.isArray(customers)) return "Select a referring customer";
    
    const customer = customers.find(c => c && c.value === value);
    return customer ? customer.label : "Select a referring customer";
  };

  return (
    <>
      <FormField
        control={form.control}
        name="lead_source"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Lead Source</FormLabel>
            <FormControl>
              <Select
                value={field.value}
                onValueChange={(val) => {
                  form.setValue("lead_source", val);
                  if (val !== "Other") {
                    form.setValue("lead_source_description", "");
                  }
                  if (val !== "Word of Mouth") {
                    form.setValue("referred_by_id", null);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select lead source..." />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_SOURCES.map((src) => (
                    <SelectItem value={src} key={src}>
                      {src}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
          </FormItem>
        )}
      />

      {watchedLeadSource === "Other" && (
        <FormField
          control={form.control}
          name="lead_source_description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Please specify other lead source</FormLabel>
              <FormControl>
                <Textarea {...field} placeholder="Describe lead source" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
          rules={{
            required:
              watchedLeadSource === "Other"
                ? "Please describe the lead source"
                : false,
          }}
        />
      )}

      {watchedLeadSource === "Word of Mouth" && (
        <FormField
          control={form.control}
          name="referred_by_id"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Referred by</FormLabel>
              
              {error && (
                <div className="text-sm text-red-500 mb-2">{error}</div>
              )}
              
              <div className="relative">
                <Popover 
                  open={isReferralOpen} 
                  onOpenChange={setIsReferralOpen}
                >
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        role="combobox"
                        className={cn(
                          "justify-between",
                          !field.value && "text-muted-foreground"
                        )}
                        disabled={isLoading}
                        type="button"
                      >
                        {isLoading ? (
                          <div className="flex items-center">
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 
                            Loading customers...
                          </div>
                        ) : (
                          getSelectedCustomerLabel(field.value)
                        )}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  
                   {/* WORKING SOLUTION: Use custom positioned dropdown */}
                   {isReferralOpen && (
                     <div 
                       ref={dropdownRef}
                       className="absolute z-50 w-full bg-white border rounded-md shadow-lg mt-1"
                       style={{
                         top: '100%',
                         left: '0',
                         maxHeight: '200px',
                         overflowY: 'auto',
                         minWidth: '300px'
                       }}
                     >
                      <div className="p-2">
                        <input 
                          type="text" 
                          placeholder="Search customers..." 
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full p-2 border rounded mb-2 text-sm"
                          autoFocus
                        />
                        
                        <div className="space-y-1">
                          {isLoading ? (
                            <div className="flex items-center justify-center p-4">
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              <span>Loading customers...</span>
                            </div>
                          ) : error ? (
                            <div className="text-red-500 p-2 text-sm">{error}</div>
                          ) : customers.length === 0 ? (
                            <div className="text-gray-500 p-2 text-sm">No customers found</div>
                          ) : (
                            customers
                              .filter(customer => 
                                !searchTerm || 
                                customer.label.toLowerCase().includes(searchTerm.toLowerCase())
                              )
                              .map((customer) => (
                                <button
                                  key={customer.value}
                                  type="button"
                                  className="w-full text-left p-2 hover:bg-gray-100 rounded text-sm flex items-center justify-between"
                                  onClick={() => {
                                    console.log('🎯 Customer selected:', customer.value);
                                    form.setValue("referred_by_id", customer.value);
                                    setIsReferralOpen(false);
                                  }}
                                >
                                  {customer.label}
                                  {field.value === customer.value && (
                                    <Check className="h-4 w-4" />
                                  )}
                                </button>
                              ))
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </Popover>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <FormField
        control={form.control}
        name="lead_owner_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Lead Owner</FormLabel>
            <FormControl>
              <Select
                value={field.value || ''}
                onValueChange={(val) => field.onChange(val)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select lead owner" />
                </SelectTrigger>
                <SelectContent>
                  {companyMembers.map((member) => (
                    <SelectItem 
                      key={member.user_id} 
                      value={member.user_id}
                    >
                      {member.first_name || 'Unknown'} {member.last_name || 'User'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
          </FormItem>
        )}
      />
    </>
  );
};
